import { useCallback, useMemo, useState } from 'react'

import type { Grower } from '@/lib/api/growers-api'
import { Combobox } from '@/components/ui/combobox'
import { useDebounced } from '@/hooks/use-debounced'
import { useInfiniteGrowersQuery } from '@/hooks/use-growers-query'
import { m } from '@/lib/paraglide/messages'

interface Props {
    /** Id gắn vào nút mở dropdown, để nhãn của bộ lọc trỏ đúng phần tử (R7).
     *  Optional nên mọi call-site cũ giữ nguyên hành vi. */
    id?: string
    /** Selected grower id. Empty string / undefined means nothing picked. */
    value?: string
    onChange: (growerID: string, grower?: Grower) => void
    onSelectedGrowerChange?: (grower: Grower | undefined) => void
    /** Name to show when `value` is not in the loaded pages — a household that
     *  was soft-deleted after the record was written. Feed it the record's own
     *  `grower_name`; without it the trigger reads as empty on old records. */
    fallbackLabel?: string
    placeholder?: string
    /** Filter usage: lets the user clear back to "all households". Leave off for
     *  data-entry forms where grower_id is required. */
    allowClear?: boolean
    disabled?: boolean
    className?: string
}

/**
 * Grower picker with server-side search and paged loading.
 *
 * Every grower dropdown in the app used to render one fully-loaded page, which
 * capped selectable households at the API's default page size — households
 * sorted later by name simply could not be chosen. Search therefore runs on the
 * server (`shouldFilter={false}` keeps cmdk from filtering the page again) and
 * the list grows as the user scrolls.
 *
 * Soft-deleted households never appear here: they are excluded by the API, which
 * is the whole point of soft-delete now that `is_active` is gone. They remain
 * visible on public traceability pages and in `fallbackLabel`.
 */
/**
 * Nhãn hiển thị của một hộ trong danh sách chọn. Nhiều hộ trùng tên khai sinh
 * nên chỉ hiện `name` là người dùng không biết chọn dòng nào; tên thường gọi
 * ghép trong ngoặc là thứ họ dùng để phân biệt ngoài thực tế.
 */
export function growerLabel(g: Pick<Grower, 'name' | 'nickname'>): string {
    return g.nickname ? `${g.name} (${g.nickname})` : g.name
}

export function GrowerSearchSelect({
    id,
    value,
    onChange,
    onSelectedGrowerChange,
    fallbackLabel,
    placeholder,
    allowClear,
    disabled,
    className,
}: Props) {
    const [search, setSearch] = useState('')
    const debouncedSearch = useDebounced(search, 300)

    const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
        useInfiniteGrowersQuery(debouncedSearch)

    const growers = useMemo(
        () => data?.pages.flatMap((p) => p.items) ?? [],
        [data],
    )

    const growersById = useMemo(
        () => new Map(growers.map((g) => [g.id, g])),
        [growers],
    )

    const options = useMemo(
        () =>
            growers.map((g) => ({
                value: g.id,
                label: growerLabel(g),
            })),
        [growers],
    )

    const selectedOption =
        value && fallbackLabel ? { value, label: fallbackLabel } : undefined

    // Ô nhập nằm trong popover và bị Radix unmount khi đóng, nên nó quay lại
    // rỗng. State `search` phải reset theo, nếu không lần mở sau người dùng
    // thấy ô tìm kiếm trống mà danh sách vẫn bị lọc theo từ khoá cũ.
    const handleOpenChange = useCallback((next: boolean) => {
        if (!next) setSearch('')
    }, [])

    // Giữ nguyên identity để Combobox không phải dựng lại IntersectionObserver
    // ở mỗi lần render.
    const handleLoadMore = useCallback(() => {
        void fetchNextPage()
    }, [fetchNextPage])

    const handleChange = useCallback(
        (nextValue: string) => {
            const selectedGrower = nextValue
                ? growersById.get(nextValue)
                : undefined
            onChange(nextValue, selectedGrower)
            onSelectedGrowerChange?.(selectedGrower)
        },
        [growersById, onChange, onSelectedGrowerChange],
    )

    return (
        <Combobox
            id={id}
            options={options}
            value={value}
            onChange={handleChange}
            onSearchChange={setSearch}
            shouldFilter={false}
            placeholder={placeholder ?? m.grower_select_placeholder()}
            searchPlaceholder={m.grower_search_placeholder()}
            emptyText={m.grower_not_found()}
            onLoadMore={handleLoadMore}
            hasMore={hasNextPage}
            loadingMore={isFetchingNextPage}
            onOpenChange={handleOpenChange}
            selectedOption={selectedOption}
            allowClear={allowClear}
            disabled={disabled}
            className={className}
        />
    )
}
